// @givmo/employer-module — the pickers' read (EP-SUITE8): `GET /admin/company/module-choices`, every
// app the company could give, each `{module, on, roles}`, decoded tolerantly (`app-choice.ts`).
//
// Global Admin only on the server (`company_admin:edit`), which is exactly who reaches the act that
// reads it, approving a join request, and it serves on the portal's host whatever the console's
// flags say (OM-57). One page is the whole list: the catalog is eight apps, the page a hundred.
//
// ⚠ ONLY THE APPS THIS BUILD SERVES, WHERE THE CALLER NAMES THEM (EP-WEB55 (c)). The console's
// access-request approval hands in its served set, the one its app grid draws (`servedModuleSlugs`),
// and the rows are narrowed to it (`servedAppChoices`): an app with no page anywhere there is not
// offered. No set, no narrowing: the old employer portal's approval (D-EP-86). The narrowed
// list keeps its identity until the read or the set's CONTENTS change, because the picker re-seats
// its draft whenever the list's identity changes, and a host may derive the set afresh on every
// render.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sdk } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { decodeAppChoices, servedAppChoices } from './app-choice';
import type { AppListStatus } from './app-choice-fields';

export const APP_CHOICES_KEY = ['companies-list_company_module_choices'] as const;

/** Joins a served set into one key; no slug carries it. */
const SERVED_KEY_SEPARATOR = '\n';

/**
 * The list for a picker that is showing: re-read on every open (`staleTime: 0`), narrowed to the apps
 * this build serves when the host names them.
 */
export function useAppChoices(enabled: boolean, served?: readonly string[]) {
  const query = useQuery({
    queryKey: APP_CHOICES_KEY,
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await sdk.companiesListCompanyModuleChoices({
        query: { 'page[limit]': 100, 'page[offset]': 0 },
        signal,
        throwOnError: true,
      });
      return decodeAppChoices(extractList<unknown>(data));
    },
    staleTime: 0,
  });
  const servedKey = served === undefined ? null : served.join(SERVED_KEY_SEPARATOR);
  const rows = useMemo(() => {
    if (query.data === undefined) return null;
    const servedSet =
      servedKey === null
        ? undefined
        : servedKey.length === 0
          ? []
          : servedKey.split(SERVED_KEY_SEPARATOR);
    return servedAppChoices(query.data, servedSet);
  }, [query.data, servedKey]);
  // A list once read stays on screen through a failed re-read; only no list at all is an error.
  const status: AppListStatus = rows !== null ? 'ready' : query.isError ? 'error' : 'loading';
  return { rows, status, refetch: () => void query.refetch() };
}
